import * as THREE from 'three';

/**
 * A 3D minimap in the bottom-left corner showing a small rotating globe
 * with markers for the player (cyan) and ship (yellow).
 */
export class Minimap {
  constructor() {
    // ── Container ──
    this.container = document.createElement('div');
    this.container.id = 'minimap';
    document.body.appendChild(this.container);

    // ── Design tokens (for canvas-drawn labels) ──
    const cs = getComputedStyle(document.documentElement);
    this._fontMono = (cs.getPropertyValue('--font-mono') || '').trim() || 'monospace';
    this._labelColor = (cs.getPropertyValue('--warn') || '').trim() || '#ffc86b';

    // ── Renderer ──
    this.size = 180;
    this.renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    this.renderer.setSize(this.size, this.size);
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.setClearColor(0x000000, 0);
    this.container.appendChild(this.renderer.domElement);

    // ── Scene ──
    this.scene = new THREE.Scene();
    this._mapGroup = new THREE.Group();
    this.scene.add(this._mapGroup);

    // ── Camera (orthographic looking at globe) ──
    const d = 2.2;
    this.camera = new THREE.OrthographicCamera(-d, d, d, -d, 0.1, 100);
    this.camera.position.set(0, 0, 5);
    this.camera.lookAt(0, 0, 0);

    // ── Lighting ──
    this.scene.add(new THREE.AmbientLight(0x666688, 0.6));
    const dirLight = new THREE.DirectionalLight(0xffffff, 1);
    dirLight.position.set(3, 3, 5);
    this.scene.add(dirLight);

    // ── Globe ──
    this.globeGeo = new THREE.IcosahedronGeometry(1.5, 3);
    this.globeMat = new THREE.MeshStandardMaterial({
      color: 0x446688,
      roughness: 0.7,
      metalness: 0.1,
      wireframe: false,
      transparent: false,
      opacity: 1,
    });
    this.globe = new THREE.Mesh(this.globeGeo, this.globeMat);
    this._mapGroup.add(this.globe);

    // Wireframe overlay for planet grid feel
    const wireMat = new THREE.MeshBasicMaterial({
      color: 0x88aacc,
      wireframe: true,
      transparent: true,
      opacity: 0.12,
    });
    this.wireframe = new THREE.Mesh(this.globeGeo, wireMat);
    this._mapGroup.add(this.wireframe);

    // ── Player marker (cyan direction arrow) ──
    const markerGeo = new THREE.ConeGeometry(0.092, 0.22, 3);
    this.playerMarker = new THREE.Mesh(
      markerGeo,
      new THREE.MeshBasicMaterial({ color: 0x00ffff })
    );
    this.playerMarker.rotation.z = -Math.PI / 2;
    this.scene.add(this.playerMarker);

    // Player glow ring
    const ringGeo = new THREE.RingGeometry(0.12, 0.18, 16);
    this.playerRing = new THREE.Mesh(
      ringGeo,
      new THREE.MeshBasicMaterial({
        color: 0x00ffff,
        transparent: true,
        opacity: 0.5,
        side: THREE.DoubleSide,
      })
    );
    this.scene.add(this.playerRing);

    // ── Ship marker (orange triangle) ──
    this.shipMarker = new THREE.Mesh(
      new THREE.ConeGeometry(0.1, 0.2, 4),
      new THREE.MeshBasicMaterial({ color: 0xff8800 })
    );
    this._mapGroup.add(this.shipMarker);

    // ── Ship label ──
    this.shipLabel = this._createLabel('SHIP');
    this._mapGroup.add(this.shipLabel);

    // State
    this.planetRadius = 100;
    this.planetCenter = new THREE.Vector3();
    this.visible = false;

    // ── Extra markers (species & ruins) ──
    this._speciesMarkerMeshes = [];
    this._ruinMarkerMeshes = [];

    // ── Player trail (Outer Wilds-style dotted path) ──
    this._trailDirs = [];          // normalized direction vectors on unit sphere
    this._trailLine = null;        // THREE.Line mesh
    this._lastTrailDir = null;     // last recorded direction for distance sampling
    this._trailMaxPoints = 600;    // cap trail length
    this._trailMinDist = 0.04;     // min angular distance between samples (~2m on globe)
  }

  _createLabel(text) {
    const canvas = document.createElement('canvas');
    canvas.width = 128;
    canvas.height = 32;
    const ctx = canvas.getContext('2d');
    ctx.font = `500 18px ${this._fontMono}`;
    ctx.fillStyle = this._labelColor;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(text, 64, 16);

    const tex = new THREE.CanvasTexture(canvas);
    tex.minFilter = THREE.LinearFilter;
    const mat = new THREE.SpriteMaterial({ map: tex, transparent: true, opacity: 0.8 });
    const sprite = new THREE.Sprite(mat);
    sprite.scale.set(0.6, 0.15, 1);
    return sprite;
  }

  /**
   * Configure the minimap for the current planet.
   */
  setPlanet(planetCenter, planetRadius, planetColor) {
    this.planetCenter = planetCenter.clone();
    this.planetRadius = planetRadius;

    if (planetColor !== undefined) {
      const col = new THREE.Color();
      if (typeof planetColor === 'number') {
        col.setHex(planetColor);
      } else if (planetColor instanceof THREE.Color) {
        col.copy(planetColor);
      } else {
        col.setHSL(planetColor.h || 0, planetColor.s || 0.5, planetColor.l || 0.5);
      }
      this.globeMat.color.copy(col);
    }
  }

  show() {
    this.visible = true;
    this.container.classList.remove('hidden');
  }

  hide() {
    this.visible = false;
    this.container.classList.add('hidden');
  }

  /**
   * Update marker positions each frame.
   * @param {THREE.Vector3} playerWorldPos    Player's world position
   * @param {THREE.Vector3} shipWorldPos      Ship's world position
   * @param {THREE.Vector3} playerFacingDir   Player's world heading vector
   */
  update(playerWorldPos, shipWorldPos, playerFacingDir) {
    if (!this.visible) return;

    // Map world positions to unit-sphere directions for the minimap
    const globeR = 1.5; // radius of the minimap globe
    const markerR = globeR + 0.12; // slightly above surface

    // Player
    const playerDir = playerWorldPos.clone().sub(this.planetCenter).normalize();
    const fixedDir = new THREE.Vector3(0, 0, 1);
    const quat = new THREE.Quaternion().setFromUnitVectors(playerDir, fixedDir);
    this._mapGroup.quaternion.copy(quat);

    this.playerMarker.position.set(0, 0, markerR);
    this.playerRing.position.copy(this.playerMarker.position);
    this.playerRing.lookAt(0, 0, 0); // face outward

    const heading = playerFacingDir ? playerFacingDir.clone().applyQuaternion(quat) : new THREE.Vector3(0, 1, 0);
    const angle = Math.atan2(heading.y, heading.x);
    this.playerMarker.rotation.z = angle - Math.PI / 2;

    // ── Record trail point ──
    this._recordTrailPoint(playerDir);

    // Ship
    const shipDir = shipWorldPos.clone().sub(this.planetCenter).normalize();
    this.shipMarker.position.copy(shipDir.clone().multiplyScalar(markerR));
    // Orient the cone to point outward
    this.shipMarker.lookAt(0, 0, 0);
    this.shipMarker.rotateX(Math.PI / 2);

    // Label above ship marker
    this.shipLabel.position.copy(shipDir.clone().multiplyScalar(markerR + 0.25));

    // The map group is rotated so the player stays centered
    // while the planet and markers move beneath them.
    this.renderer.render(this.scene, this.camera);
  }

  // ── Player trail ──

  _recordTrailPoint(dir) {
    if (!this._lastTrailDir) {
      this._lastTrailDir = dir.clone();
      this._trailDirs.push(dir.clone());
      this._rebuildTrailLine();
      return;
    }

    const angDist = dir.angleTo(this._lastTrailDir);
    if (angDist < this._trailMinDist) return;

    this._trailDirs.push(dir.clone());
    this._lastTrailDir.copy(dir);

    // Cap trail length
    if (this._trailDirs.length > this._trailMaxPoints) {
      this._trailDirs.shift();
    }

    this._rebuildTrailLine();
  }

  _rebuildTrailLine() {
    // Remove old line
    if (this._trailLine) {
      this.scene.remove(this._trailLine);
      this._trailLine.geometry?.dispose();
      this._trailLine.material?.dispose();
      this._trailLine = null;
    }

    if (this._trailDirs.length < 2) return;

    const globeR = 1.5;
    const trailR = globeR + 0.04; // just above surface
    const points = this._trailDirs.map(d => d.clone().multiplyScalar(trailR));

    const geometry = new THREE.BufferGeometry().setFromPoints(points);
    const material = new THREE.LineDashedMaterial({
      color: 0xff4444,
      dashSize: 0.06,
      gapSize: 0.04,
      transparent: true,
      opacity: 0.7,
    });

    this._trailLine = new THREE.Line(geometry, material);
    this._trailLine.computeLineDistances(); // required for dashed lines
    this._mapGroup.add(this._trailLine);
  }

  clearTrail() {
    this._trailDirs = [];
    this._lastTrailDir = null;
    if (this._trailLine) {
      this._mapGroup.remove(this._trailLine);
      this._trailLine.geometry?.dispose();
      this._trailLine.material?.dispose();
      this._trailLine = null;
    }
  }

  dispose() {
    this.clearTrail();
    this.renderer.dispose();
    this.container.remove();
  }

  /**
   * Set markers for creature species clusters and ruins on the minimap.
   * @param {Array<{position: Vector3, listened: boolean}>} speciesMarkers
   * @param {Array<{position: Vector3, interacted: boolean}>} ruinMarkers
   */
  setMarkers(speciesMarkers = [], ruinMarkers = []) {
    const globeR = 1.5;
    const markerR = globeR + 0.08;

    // ── Species markers (green=listened, white=undiscovered) ──
    // Remove old
    for (const m of this._speciesMarkerMeshes) {
      this._mapGroup.remove(m);
      m.geometry?.dispose();
      m.material?.dispose();
    }
    this._speciesMarkerMeshes = [];

    for (const s of speciesMarkers) {
      const dir = s.position.clone().sub(this.planetCenter).normalize();
      const color = s.listened ? 0x44ff88 : 0xffffff;
      const geo = new THREE.SphereGeometry(0.06, 6, 6);
      const mat = new THREE.MeshBasicMaterial({ color, transparent: true, opacity: s.listened ? 0.8 : 0.3 });
      const mesh = new THREE.Mesh(geo, mat);
      mesh.position.copy(dir.multiplyScalar(markerR));
      this._mapGroup.add(mesh);
      this._speciesMarkerMeshes.push(mesh);
    }

    // ── Ruin markers (amber=unsearched, dim=interacted) ──
    for (const m of this._ruinMarkerMeshes) {
      this._mapGroup.remove(m);
      m.geometry?.dispose();
      m.material?.dispose();
    }
    this._ruinMarkerMeshes = [];

    for (const r of ruinMarkers) {
      const dir = r.position.clone().sub(this.planetCenter).normalize();
      const color = r.interacted ? 0x665533 : 0xffaa44;
      const geo = new THREE.OctahedronGeometry(0.07, 0);
      const mat = new THREE.MeshBasicMaterial({ color, transparent: true, opacity: r.interacted ? 0.25 : 0.85 });
      const mesh = new THREE.Mesh(geo, mat);
      mesh.position.copy(dir.multiplyScalar(markerR));
      this._mapGroup.add(mesh);
      this._ruinMarkerMeshes.push(mesh);
    }
  }
}
